import {
  feedbackListResponseSchema,
  feedbackReasonsResponseSchema,
  feedbackSubmitResponseSchema,
  paths,
  type FeedbackDraft,
  type FeedbackReason,
  type FeedbackSnapshot,
} from "@go-novus/admin-api";
import {
  MerchantFeedbackNetworkError,
  type MerchantFeedbackClient,
} from "@go-novus/widget";

import {
  readBackendFailure,
  requestBackendResult,
  throwBackendFailure,
  unwrapBackendResult,
} from "#/shared/lib/backend-api";

const loadReasonsOperation = "feedback.reasons";
const loadMessagesOperation = "feedback.messages";
const submitFeedbackOperation = "feedback.submit";

/**
 * Browser only talks to the same-origin Shopify BFF. Tenant and member identity
 * stay in the HttpOnly admin session and are added by the BFF.
 */
export function createMerchantFeedback(
  source: "inbox" | "playground",
): MerchantFeedbackClient {
  let reasonsRequest: Promise<FeedbackReason[]> | undefined;
  const loadReasons = () =>
    (reasonsRequest ??= unwrapBackendResult(
      requestBackendResult({
        method: "GET",
        operation: loadReasonsOperation,
        parse: (payload) =>
          feedbackReasonsResponseSchema.parse(payload).reasons,
        path: paths.feedbackReasons,
      }),
    ).catch((error: unknown) => {
      reasonsRequest = undefined;
      throw error;
    }));

  const load = async (messageIds: string[]): Promise<FeedbackSnapshot[]> => {
    const rows: FeedbackSnapshot[] = [];
    for (let offset = 0; offset < messageIds.length; offset += 100) {
      const query = new URLSearchParams();
      for (const id of messageIds.slice(offset, offset + 100)) {
        query.append("message_id", id);
      }
      const response = await requestBackendResult({
        method: "GET",
        operation: loadMessagesOperation,
        parse: (payload) => feedbackListResponseSchema.parse(payload),
        path: `${paths.feedbackMessages}?${query}`,
      }).match((value) => value, throwBackendFailure);
      rows.push(...response.feedback);
    }
    return rows;
  };

  void loadReasons().catch(() => undefined);

  return {
    align: source === "playground" ? "start" : "end",
    loadReasons,
    load,
    submit: async (messageId: string, draft: FeedbackDraft) => {
      try {
        return (
          await unwrapBackendResult(
            requestBackendResult({
              body: { source, ...draft },
              method: "POST",
              operation: submitFeedbackOperation,
              parse: (payload) => feedbackSubmitResponseSchema.parse(payload),
              path: paths.feedbackMessage(messageId),
            }),
          )
        ).feedback;
      } catch (error) {
        const failure = readBackendFailure(error);
        if (
          failure?.code === "backend.network" ||
          (failure && "status" in failure && failure.status === 503)
        ) {
          const [snapshot] = await load([messageId]).catch(() => []);
          if (snapshot) return snapshot;
        }
        if (failure && "status" in failure && failure.status === 422) {
          reasonsRequest = undefined;
          await loadReasons().catch(() => undefined);
        }
        if (failure?.code === "backend.network") {
          throw new MerchantFeedbackNetworkError("feedback network failure", {
            cause: error,
          });
        }
        throw error;
      }
    },
  };
}
